import { useContext, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { AuthContext } from "~/utils/useAuth";
import { styled, keyframes, vh, darkTheme } from "~/ui/style/stitches.config";
import { getJournalEntries, deleteJournalEntry, updateJournalEntry, JournalEntry } from "~/api/journalApi";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { QuestionHover } from "~/ui/components/Tooltip";
import { isIOSNativeApp } from "~/utils/deviceDetection";
import { motion } from "framer-motion";
import { JournalOverlay } from "./JournalOverlay";
import { JournalFilters } from "./JournalFilters";
import { CreateJournalEntryOverlay } from "./CreateJournalEntryOverlay";
import { expandSearchTerms, searchInText } from "~/utils/searchUtils";
import { trackEvent } from "~/api/analyticsApi";
import { JournalFeatureHeader } from "./JournalFeatureHeader";
import { imageUrl } from "~/utils/imageurl";
import { Button } from "~/ui/components/Button";

const fadeIn = keyframes({
  '0%': { opacity: 0, transform: 'translateY(10px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' }
});

const fadeOut = keyframes({
  '0%': { opacity: 1, transform: 'scale(1)' },
  '100%': { opacity: 0, transform: 'scale(0.97)' }
});

const deckFolderToName: { [key: string]: string } = {
  'dirt': 'Summer of Bibliomancy',
  'viator-somniorum': 'Viator Somniorum',
  'internet-lore': 'Internet Lore Tarot',
  'mixed-signals': 'Mixed Signals',
  'mushroom': 'Mushroom Tarot',
  'gothmancy': 'Gothmancy Tarot',
  'black': 'The Black Deck',
  'eldritch': 'Eldritch Overload',
  'rider-waite': '',
  'marseille': '',
  'frutiger': 'Frutiger Tarot',
  // Add more mappings as needed
};

const getDeckNameFromPath = (imagePath: string) => {
  if (!imagePath?.includes('/decks/')) return '';

  const folderName = imagePath.split('/decks/')[1]?.split('/')[0];
  return deckFolderToName[folderName] || '';
};

const JOURNAL_CUTOFF_DATE = new Date('2025-10-01');

export const Journal = ({ entryId }: { entryId?: string }) => {
  const { appDarkMode: dk } = useSnapshot(localState);
  const { user } = useContext(AuthContext);
  const [, setLocation] = useLocation();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [entriesLoaded, setEntriesLoaded] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const selectedEntry = entryId ? entries.find(e => e.id === entryId) || null : null;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showUpdatedToast, setShowUpdatedToast] = useState(false);
  const [monthFilter, setMonthFilter] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState<string | null>(null);
  const [showCreateOverlay, setShowCreateOverlay] = useState(false);
  const ENTRIES_PER_PAGE = 20;
  const [visibleCount, setVisibleCount] = useState(ENTRIES_PER_PAGE);

  useEffect(() => {
    const fetchEntries = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const data = await getJournalEntries();
        setEntries(data);
        setEntriesLoaded(true);
      } catch (error) {
        console.error('Failed to fetch journal entries:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      trackEvent("/journal/visited_page");
      fetchEntries();
    }
  }, [user?.uuid]); // More specific dependency

  const handleDelete = async (entryId: string) => {
    trackEvent("/journal/delete_entry");
    try {
      setDeletingId(entryId);
      setTimeout(async () => {
        await deleteJournalEntry(entryId);
        setEntries(entries.filter(entry => entry.id !== entryId));
        setDeletingId(null);
        setShowDeleteConfirm(null);
        setOpenMenuId(null);
      }, 300);
    } catch (error) {
      console.error('Failed to delete entry:', error);
      setDeletingId(null);
    }
  };

  const handleSaveEntry = async (entryId: string, updatedContent: any) => {
    try {
      await updateJournalEntry(entryId, updatedContent);
      setEntries(entries.map(entry =>
        entry.id === entryId
          ? { ...entry, content: updatedContent }
          : entry
      ));

      // Show updated toast
      setShowUpdatedToast(true);
      setTimeout(() => {
        setShowUpdatedToast(false);
      }, 4000);
    } catch (error) {
      console.error('Failed to update entry:', error);
      throw error;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const day = date.getDate();
    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toUpperCase();

    return { month, day, time };
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.menu-container')) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuId]);

  useEffect(() => {
    if (isIOSNativeApp()) {
      // Hide TopBar and home button completely for iOS native app
      const topBar = document.querySelector('[data-darkmode]') as HTMLElement;
      const homeButton = document.querySelector('.home-button') as HTMLElement;

      if (topBar) {
        topBar.style.display = 'none';
      }
      if (homeButton) {
        homeButton.style.display = 'none';
      }

      return () => {
        if (topBar) {
          topBar.style.display = 'flex';
        }
        if (homeButton) {
          homeButton.style.display = 'flex';
        }
      };
    }
  }, []);

  // Filter entries based on month and search
  useEffect(() => {
    let filtered = [...entries];

    // Always hide entries before cut off date
    filtered = filtered.filter(entry => {
      const entryDate = new Date(entry.createdAt);
      return entryDate >= JOURNAL_CUTOFF_DATE;
    });

    // Apply month filter
    if (monthFilter) {
      filtered = filtered.filter(entry => {
        const date = new Date(entry.createdAt);
        const entryMonth = String(date.getMonth() + 1).padStart(2, '0');
        return entryMonth === monthFilter;
      });
    }

    // Apply search filter
    if (searchFilter) {
      const expandedSearchTerms = expandSearchTerms(searchFilter);

      filtered = filtered.filter(entry => {
        const content = entry.content;
        if (!content) return false;

        const searchableText = [
          content.query,
          content.gist,
          content.overall,
          content.question,
          content.reflections,
          content.cards?.map((card: any) => card.name).join(' '),
          content.cards?.map((card: any) => card.position).join(' ')
        ].filter(Boolean).join(' ');

        return searchInText(expandedSearchTerms, searchableText);
      });
    }

    setFilteredEntries(filtered);
    setVisibleCount(ENTRIES_PER_PAGE);
  }, [entries, monthFilter, searchFilter]);

  return (
    <StyledJournalPage isIOSNative={isIOSNativeApp()}>
      {showUpdatedToast && (
        <div
          style={{
            position: 'fixed',
            top: '90px',
            left: '0',
            right: '0',
            margin: '0 auto',
            width: 'fit-content',
            backgroundColor: dk ? 'var(--colors-surface)' : '#fff',
            color: dk ? '#fff' : '#000',
            padding: '8px 12px',
            borderRadius: '10px',
            fontSize: '12px',
            opacity: 0,
            animation: `${fadeIn} 0.3s ease forwards .3s, ${fadeOut} 0.3s ease forwards 2.2s`,
            border: dk ? '1px solid rgba(255, 255, 255, 0.4)' : '0px solid #ddd',
            zIndex: 1000000,
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
        >
          Journal updated ✓
        </div>
      )}

      <StyledJournalContent>
        <JournalFeatureHeader onNewEntry={() => {
          setShowCreateOverlay(true);
        }} />

        {/* Add filters */}
        {entriesLoaded && (() => {
          const entriesAfterCutoff = entries.filter(entry => {
            const entryDate = new Date(entry.createdAt);
            return entryDate >= JOURNAL_CUTOFF_DATE;
          });
          return entriesAfterCutoff.length > 0;
        })() && (
            <JournalFilters
              entries={entries}
              onMonthFilter={setMonthFilter}
              onSearchFilter={setSearchFilter}
            />
          )}

        <div className="entries-container">
          {loading && user ? (
            <div className="loading-container">
              <SimpleLoadingIndicator
                style={{
                  position: 'static',
                  marginTop: '0',
                  width: '24px',
                  height: '24px',
                  opacity: 0.5,
                }}
              />
            </div>
          ) : (() => {
            const entriesAfterCutoff = entries.filter(entry => {
              const entryDate = new Date(entry.createdAt);
              return entryDate >= JOURNAL_CUTOFF_DATE;
            });

            // No entries at all OR only entries before cutoff
            if ((entries.length === 0 || entriesAfterCutoff.length === 0) && entriesLoaded) {
              return (
                <div className="empty-state">
                  <div className="empty-title">
                    Create a summary in a tarot room and it will save here.
                  </div>
                  {user && (
                    <Button
                      Theme="dark"
                      style={{
                        marginTop: "20px",
                        padding: "12px 20px",
                        fontSize: "12px",
                        borderRadius: "12px",
                      }}
                      onClick={() => {
                        trackEvent("/journal/create_room");
                        window.location.href = "/create-room";
                      }}
                    >
                      Open a room
                    </Button>
                  )}
                </div>
              );
            }

            // Has displayable entries but filtered out
            if (filteredEntries.length === 0 && entriesAfterCutoff.length > 0 && entriesLoaded) {
              return (
                <div className="empty-state">
                  <div className="empty-title">
                    No readings match these filters
                  </div>
                </div>
              );
            }

            // Show entries
            return (
              <>
                {filteredEntries.slice(0, visibleCount).map((entry, index) => {
                  const { month, day, time } = formatDate(entry.createdAt);

                  return (
                    <div key={entry.id}>
                      <EntryCard
                        key={entry.id}
                        as={motion.div}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, scale: 0.97 }}
                        transition={{
                          duration: 0.5,
                          delay: index < 10 ? index * 0.05 : 0,
                          ...(deletingId === entry.id && { duration: 0.4, delay: 0 })
                        }}
                        className={deletingId === entry.id ? 'deleting' : ''}
                        onClick={() => {
                          trackEvent("/journal/open_entry");
                          setLocation(`/journal/${entry.id}`);
                        }}
                      >
                        {/* Loading overlay when deleting */}
                        {deletingId === entry.id && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: dk ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                            borderRadius: '26px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10
                          }}>
                            <SimpleLoadingIndicator
                              style={{
                                width: '24px',
                                height: '24px',
                                position: 'static',
                                marginTop: '0',
                                opacity: 0.7
                              }}
                            />
                          </div>
                        )}

                        {/* Date in upper left */}
                        <div className="entry-date">
                          <div className="month">{month}</div>
                          <div className="day">{day}</div>
                        </div>

                        {/* Three-dot menu in upper right */}
                        <div className="menu-container">
                          <div
                            className="menu-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === entry.id ? null : entry.id);
                            }}
                          >
                            ⋯
                          </div>

                          {openMenuId === entry.id && (
                            <div className="menu-dropdown" onClick={(e) => e.stopPropagation()}>
                              <div
                                className="close-button"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <img src="/images/more-menu/arrow.svg" />
                              </div>

                              <div className="delete-text">Remove entry?</div>
                              <button
                                className="delete-button"
                                onClick={() => handleDelete(entry.id)}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Cards - responsive display */}
                        {entry.content?.cards && entry.content.cards.length > 0 && (
                          <div className="cards-row">
                            {entry.content.cards.map((card: any, cardIndex: number) => {
                              const isReversed = card.name.includes('Reversed') || card.name.includes('(Reversed)');

                              return (
                                <div key={cardIndex} className="card-wrapper">
                                  <img
                                    src={card.imagePath.replace(/w=\d+|w=undefined/, 'w=200')}
                                    alt={card.name}
                                    className="card-image"
                                    style={{
                                      transform: isReversed ? 'rotate(180deg)' : 'none'
                                    }}
                                  />
                                  <div className="card-name">
                                    {card.name.replace(/ \(Reversed\)| Reversed/g, ' (R)')}
                                    {isReversed && ''}
                                    {card.position && (
                                      <div className="card-position">
                                        {card.position}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}

                            {/* Gradient overlay to fade last card */}
                            <div className="cards-gradient-overlay" />
                          </div>
                        )}

                        {/* Query text */}
                        {entry.content?.query && (
                          <div className="query-text">
                            {entry.content.query}
                          </div>
                        )}

                        {/* Gist text */}
                        {(entry.content?.gist) && (
                          <div className="gist-container">
                            <div className="gist-text">
                              {entry.content.gist}
                            </div>
                          </div>
                        )}

                        {/* Reflection text */}
                        {entry.content?.reflections && (
                          <div className="reflection-container">
                            <img
                              src={imageUrl(`/profile-pics/${user?.profilePic}`, 80)}
                              alt="Profile"
                              className="reflection-profile-pic"
                            />
                            <svg className="chat-triangle" width="12" height="18" viewBox="0 0 14 18">
                              <path d="M2 9L12 2V16L2 9Z" fill="#f6f6f6" stroke="#f6f6f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div className="reflection-text">
                              {entry.content.reflections}
                            </div>
                          </div>
                        )}


                        {/* Open entry button */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          padding: '8px',
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLocation(`/journal/${entry.id}`);
                            }}
                            style={{
                              padding: '9px 20px',
                              borderRadius: '20px',
                              border: 'none',
                              backgroundColor: dk ? '#ffffff' : '#000000',
                              color: dk ? '#000000' : '#ffffff',
                              fontSize: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = dk ? '#dddddd' : '#3f3f3f';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = dk ? '#ffffff' : '#000';
                            }}
                          >
                            read more
                          </button>
                        </div>

                        {/* Three-dot menu in bottom right */}
                        <div className="menu-container">
                          <div
                            className="menu-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === entry.id ? null : entry.id);
                            }}
                          >
                            ⋯
                          </div>

                          {openMenuId === entry.id && (
                            <div className="menu-dropdown" onClick={(e) => e.stopPropagation()}>
                              <div
                                className="close-button"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <img src="/images/more-menu/arrow.svg" />
                              </div>

                              <div className="delete-text">Remove entry?</div>
                              <button
                                className="delete-button"
                                onClick={() => handleDelete(entry.id)}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </EntryCard>
                    </div>
                  );
                })}
                {visibleCount < filteredEntries.length && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0 30px' }}>
                    <button
                      className="show-more-btn"
                      onClick={() => setVisibleCount(prev => prev + ENTRIES_PER_PAGE)}
                    >
                      Show more
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </StyledJournalContent>

      {/* Journal overlay */}
      {selectedEntry && (
        <JournalOverlay
          entry={selectedEntry}
          onClose={() => setLocation('/journal')}
          onSave={(updatedContent) => handleSaveEntry(selectedEntry.id, updatedContent)}
        />
      )}

      {/* Create journal entry overlay */}
      {showCreateOverlay && (
        <CreateJournalEntryOverlay
          onClose={() => setShowCreateOverlay(false)}
          onCreated={async (entry) => {
            setEntries(prev => [entry, ...prev]);
            setShowCreateOverlay(false);
            setLocation(`/journal/${entry.id}`);
            try {
              const data = await getJournalEntries();
              setEntries(data);
            } catch (error) {
              console.error('Failed to refresh journal entries:', error);
            }
          }}
        />
      )}
    </StyledJournalPage>
  );
};

const StyledJournalPage = styled("div", {
  width: "100vw",
  height: vh(100),
  display: "flex",
  flexDirection: "column",
  position: "relative",
  backgroundColor: "$wash",
  color: "#000",
  [`.${darkTheme.className} &`]: { backgroundColor: "$appBg", color: "$text", "& .home-button": { backgroundColor: "$appBg", color: "$text", borderColor: "rgba(255,255,255,0.6)" }, "& .page-title": { color: "$text" }, "& .show-more-btn": { backgroundColor: "$subtleFill", color: "$text", "&:hover": { backgroundColor: "#fff", color: "#000" } } },
  userSelect: "none",
  WebkitUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  WebkitTouchCallout: "none",
  WebkitTapHighlightColor: "transparent",

  "@notmobile": {
    paddingLeft: "$navWidth",
  },

  "& .home-button": {
    backgroundColor: "$wash",
    border: "1px solid #000",
    position: "absolute",
    top: "70px",
    left: "5%",
    padding: "0 16px 0 14px",
    height: "40px",
    borderRadius: "1rem",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    zIndex: 10,
    fontSize: "12px",
    color: "#000",

    "& .arrow": {
      marginRight: "8px",
      fontSize: "16px",
      fontWeight: "200",
    },
  },

  variants: {
    isIOSNative: {
      true: {
        "@notmobile": {
          paddingLeft: 0,
        },
      },
    },
  },
});

const StyledJournalContent = styled("div", {
  flex: 1,
  padding: "40px",
  paddingTop: "80px",
  overflow: "auto",
  scrollbarGutter: "stable",

  "&::-webkit-scrollbar": {
    width: "0.5rem",
    height: "0.5rem",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "$grayA200",
    borderRadius: "0.5rem",
  },
  [`.${darkTheme.className} &::-webkit-scrollbar-thumb`]: {
    backgroundColor: "$whiteA500",
  },

  "@mobile": {
    paddingLeft: "20px",
    paddingRight: "20px",
    paddingTop: "60px",
  },

  "& .page-title": {
    fontSize: "48px",
    fontWeight: "500",
    marginBottom: "60px",
    fontFamily: "GorgeousBaby",
    textAlign: "center",
    color: "#000",

    "@mobile": {
      marginTop: "30px",
      marginBottom: "60px",
    },
  },

  "& .entries-container": {
    maxWidth: "520px",
    margin: "0 auto",
    paddingBottom: "80px",

    "@mobile": {
      paddingBottom: "160px",
    },
  },

  "& .loading-container": {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '60px',
  },

  "& .show-more-btn": {
    padding: "9px 20px",
    fontSize: "11px",
    border: "none",
    borderRadius: "20px",
    backgroundColor: "#e4e4e4",
    cursor: "pointer",
    transition: "all 0.2s ease",

    "&:hover": {
      backgroundColor: "#000",
      color: "#fff",
    },
  },

  "& .empty-state": {
    textAlign: "center",
    marginTop: "60px",

    "& .empty-title": {
      fontSize: "13px",
      lineHeight: "1.5",
      margin: "0 auto",

      "@mobile": {
        maxWidth: "250px",
      },
    },
  },
});

const EntryCard = styled("div", {
  position: "relative",
  backgroundColor: "rgba(255, 255, 255, 1)",
  border: "1px solid #000",
  borderRadius: "26px",
  padding: "26px",
  marginBottom: "16px",
  transition: "all 0.2s ease",
  minHeight: "200px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  userSelect: "none",
  WebkitUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  WebkitTouchCallout: "none",
  WebkitTapHighlightColor: "transparent",
  cursor: "pointer",

  "&:hover": {
    transform: "translateY(3px)",
  },

  "&.deleting": {
    animation: `${fadeOut} 0.4s ease forwards`,
  },

  "& .entry-date": {
    position: "absolute",
    top: "19px",
    left: "22px",
    textAlign: "center",
    fontSize: "12px",
    color: "$darkwash",
    justifyItems: "center",
    opacity: 0.7,
    zIndex: 2,

    "& .month": {
      fontSize: "11px",
      fontWeight: "400",
      letterSpacing: "0.3px",
    },

    "& .day": {
      fontSize: "12px",
      fontWeight: "400",
      lineHeight: "1",
      marginTop: "1px",
    },

    "& .time": {
      fontSize: "10px",
      marginTop: "2px",
      letterSpacing: "0.3px",
      color: "#666",
      textTransform: "uppercase",
    },
  },

  "& .query-icon": {
    position: "absolute",
    top: "59px",
    right: "31px",
    zIndex: 3,

    "& img.tooltip-arrow": {
      left: "auto",
      right: "-8px",
      transform: "none"
    }
  },

  "& .query-text": {
    fontSize: "21px",
    lineHeight: "1.1",
    marginBottom: "8px",
    paddingRight: "10%",
    fontFamily: "KeplerLightCondensed",
    fontWeight: 100,
    userSelect: "text",
    WebkitUserSelect: "text",
    MozUserSelect: "text",
    msUserSelect: "text",
    WebkitTouchCallout: "default",
    "@mobile": {
      fontSize: "22px",
    },
  },

  "& .gist-text": {
    backgroundColor: "rgba(129, 129, 129, 0.1)",
    border: "1px solid rgba(0, 0, 0, 0.3)",
    padding: "14px 16px",
    width: "93%",
    borderRadius: "14px",
    fontSize: "12px",
    lineHeight: "1.4",
    marginBottom: "20px",
    userSelect: "text",
    WebkitUserSelect: "text",
    MozUserSelect: "text",
    msUserSelect: "text",
    WebkitTouchCallout: "default",
    "@mobile": {
      fontSize: "13px",
    },
  },

  "& .reflection-container": {
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
    marginBottom: "15px",
    marginTop: "-5px",
    width: "93%",
  },

  "& .chat-triangle": {
    marginTop: "12px",
    flexShrink: 0,
    marginRight: "-8px",
    zIndex: 1,
  },


  "& .reflection-profile-pic": {
    width: "36px",
    height: "36px",
    borderRadius: "100%",
    flexShrink: 0,
    marginTop: "4px",
    marginRight: "2px",
  },

  "& .reflection-text": {
    backgroundColor: "#f6f6f6",
    padding: "16px 16px",
    borderRadius: "20px",
    fontSize: "11px",
    lineHeight: "1.4",
    color: "#333",
    flex: 1,
    position: "relative",
    userSelect: "text",
    WebkitUserSelect: "text",
    MozUserSelect: "text",
    msUserSelect: "text",
    WebkitTouchCallout: "default",

    "@mobile": {
      fontSize: "12px",
    },
  },

  "& .cards-row": {
    display: "flex",
    gap: "10px",
    marginTop: "40px",
    marginLeft: "0px",
    marginBottom: "20px",
    position: "relative",
    width: "92%",
    overflow: "hidden",

    "& .cards-gradient-overlay": {
      position: "absolute",
      right: "0",
      top: "0",
      bottom: "0",
      width: "80px",
      background: "linear-gradient(to right, transparent 30%, #fff 100%)",
      pointerEvents: "none",
    },

    "& .card-wrapper": {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      flexShrink: 0,
    },

    "& .card-image": {
      width: "60px",
      height: "auto",
      borderRadius: "6px",
      marginBottom: "4px",
      border: "1px solid rgba(0, 0, 0, 0.25)",
    },

    "& .card-name": {
      fontSize: "9px",
      lineHeight: "1.2",
      textAlign: "center",
      letterSpacing: "-0.3px",
      color: "$gray800",
      textTransform: "uppercase",
      wordBreak: "break-word",
      hyphens: "none",
      width: "60px",
      marginTop: "5px",

      "& .card-position": {
        fontSize: "8px",
        marginTop: "2px",
        textTransform: "none",
        letterSpacing: "0.05px",
        opacity: 0.5,
        color: "$gray600",
      },
    },
  },

  "& .menu-container": {
    position: "absolute",
    top: "10px",
    right: "16px",

    "& .menu-button": {
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      borderRadius: "8px",
      fontSize: "16px",
      color: "#333",
      transition: "background-color 0.2s ease",

      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.05)",
      },
    },

    "& .menu-dropdown": {
      position: "absolute",
      top: "40px",
      right: "0",
      width: "140px",
      padding: "30px 14px 10px 14px",
      borderRadius: "18px",
      backgroundColor: "#fff",
      border: "1px solid #333",
      color: "#000",
      zIndex: 10,

      "& .close-button": {
        position: "absolute",
        top: "10px",
        right: "10px",
        width: "20px",
        height: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transform: "rotate(270deg)",

        "& img": {
          width: "20px",
          height: "20px",
          filter: "none",
        },
      },

      "& .delete-text": {
        fontSize: "12px",
        marginTop: "12px",
      },

      "& .delete-button": {
        width: "100%",
        padding: "9px 16px",
        fontSize: "12px",
        textAlign: "center",
        border: "none",
        backgroundColor: "#e5e5e5",
        color: "#000",
        borderRadius: "20px",
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        marginTop: "14px",
        marginBottom: "10px",

        "&:hover": {
          backgroundColor: "#f5f5f5",
        },
      },

      "& .deleting-state": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        marginTop: "8px",
      },
    },
  },

  [`.${darkTheme.className} &`]: {
    backgroundColor: "$surface",
    borderColor: "rgba(255, 255, 255, 0.4)",
    color: "$text",
    "& .entry-date": { color: "$text" },
    "& .entry-date .time": { color: "$textMuted" },
    "& .gist-text": { borderColor: "rgba(255, 255, 255, 0.3)", backgroundColor: "rgba(255, 255, 255, 0.12)" },
    "& .reflection-text": { backgroundColor: "$subtleFill", color: "$text" },
    "& .chat-triangle path": { fill: "#383838", stroke: "#383838" },
    "& .cards-gradient-overlay": { background: "linear-gradient(to right, transparent 30%, #000 100%) !important" },
    "& .card-image": { borderColor: "rgba(255, 255, 255, 0.25)" },
    "& .cards-row .card-name": { color: "$text", "& .card-position": { color: "$text", opacity: 0.7 } },
    "& .query-text, & .gist-text, & .gist-container": { color: "$text" },
    "& .menu-container .menu-button": { color: "#fff", "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.12)" } },
    "& .menu-container .menu-dropdown": { backgroundColor: "#000", borderColor: "rgba(255, 255, 255, 0.6)", color: "$text", boxShadow: "0 4px 16px rgba(0, 0, 0, 0.4)", "& .close-button img": { filter: "invert(1)" }, "& .delete-button": { backgroundColor: "#e5e5e5", color: "#000", "&:hover": { backgroundColor: "#d0d0d0" } } },
  },
});

const StyledSummaryOverlay = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  height: vh(100),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 999999,

  '& .summary-container': {
    backgroundColor: 'inherit',
    borderRadius: '24px',
    padding: '32px',
    maxWidth: '460px',
    width: '90%',
    maxHeight: vh(84),
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '6px',
      backgroundColor: 'rgba(127, 127, 127, .5)',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
      marginTop: '24px',
      marginBottom: '24px',
    },
    '&::-webkit-scrollbar-corner': {
      backgroundColor: 'transparent',
    },
  },

  '& .summary-content': {
    padding: '28px 16px',
    paddingBottom: '120px',
    position: 'relative',
    width: '100%',
    minHeight: 'min-content',
  },

  '@mobile': {
    backgroundColor: 'transparent !important',
    '& .summary-container': {
      border: 'none !important',
      borderRadius: 0,
      width: '100%',
      height: '100%',
      maxHeight: '100%!important',
      minHeight: `calc(${vh(100)} - 140px)`,
      margin: 0,
      paddingTop: '48px!important',
    },
  },

  variants: {
    Theme: {
      light: {
        backgroundColor: 'rgba(180, 180, 180, 0.4)',
        '& .summary-container': {
          backgroundColor: '$wash',
          border: "1px solid $gray600"
        },
      },
      dark: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        '& .summary-container': {
          backgroundColor: '$darkwash',
          color: '#fff',
          border: "1px solid $gray600"
        },
        '& .x-button': {
          filter: 'invert(1)',
        }
      },
    },
  },
});

const StyledAnalysis = styled('div', {
  userSelect: 'text',
  WebkitUserSelect: 'text',
  MozUserSelect: 'text',
  msUserSelect: 'text',
  paddingTop: '10px',

  '& img': {
    width: "30%",
    borderRadius: "8px",
    marginBottom: "10px",
    maxWidth: "100px"
  },
  '& h1': {
    fontSize: '27px',
    fontFamily: 'KeplerLightCondensed',
    marginBottom: '3px',
    marginTop: "10px",
    fontWeight: 100
  },
  '& h2': {
    fontSize: '27px',
    fontFamily: 'KeplerLightCondensed',
    marginTop: '2.2rem',
    marginBottom: '0.5rem',
    fontWeight: 100
  },
  '& p': {
    fontSize: "13px",
    lineHeight: '1.4',
    margin: '4px auto 4px',
  },
  '& .interpretation': {
    marginBottom: "30px"
  },
  '& .overall': {
    backgroundColor: 'rgba(129, 129, 129, 0.1)',
    border: '1px solid $gray500',
    padding: '1.2rem',
    borderRadius: '12px'
  },
  '& .query': {
    marginBottom: '30px',
    marginTop: '-26px',
    background: '#e9e9e9',
    padding: '6px 10px',
    borderRadius: '20px',
    textAlign: 'center',
    '& p': {
      fontSize: '11px',
      opacity: 0.6,
    }
  },
  '& .gist': {
    border: '0px solid $gray500',
    padding: '.6rem',
    borderRadius: '120px',
    textAlign: 'center',
    backgroundColor: '#ededed',
    color: '#000',
    fontSize: '11px',
    fontStyle: 'italic',
    letterSpacing: '0.02em'
  },
  '& .question': {
    fontWeight: "500",
  },
  '& .card-section': {
    display: 'flex',
    gap: '20px',
    marginBottom: '30px',
    alignItems: 'flex-start',
  },
  '& .card-content': {
    flex: 1,
  },

  variants: {
    Theme: {
      dark: {
        '& .overall': {
          backgroundColor: '$gray800'
        }
      },
      light: {}
    }
  }
});

const StyledSummaryFooter = styled('div', {
  position: 'sticky',
  bottom: '-32px',
  left: 0,
  right: 0,
  width: 'calc(100% + 64px)',
  marginLeft: '-32px',
  marginRight: '-32px',
  height: '120px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderTop: '1px solid',
  padding: '12px',
  marginTop: 'auto',
  marginBottom: '-32px',

  variants: {
    Theme: {
      light: {
        backgroundColor: '$wash',
        borderColor: '$gray400',
      },
      dark: {
        backgroundColor: '$darkwash',
        borderColor: '$gray600',
      }
    }
  }
});
